import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Button,
  Stack,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  Search,
  Business,
  Work,
  Category,
  CalendarToday,
  ArrowBack,
  FilterList
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { apiUrl, fileUrl } from "../config/api";
import CustomCard from "../components/CustomCard";
import useSEO from "../hooks/useSEO";

const CompaniesPage = () => {
  const navigate = useNavigate();
  const { companyName } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // SEO
  useSEO({
    title: companyName
      ? `${decodeURIComponent(companyName)} - Компания | JobMD`
      : 'Компании-работодатели | JobMD',
  });

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyJobs, setCompanyJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeLetter, setActiveLetter] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const COMPANIES_PER_PAGE = 100;

  const LATIN_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // Загрузка компаний — плавно: без мигания, без лоадера при поиске/букве
  const loadCompanies = async (search = "", reset = true, letter = null) => {
    if (reset) {
      if (companies.length === 0) {
        setLoading(true);
      } else {
        setIsTransitioning(true); // fade
      }
    }
    try {
      const currentOffset = reset ? 0 : offset;
      let url = `${apiUrl("companies.php")}?limit=${COMPANIES_PER_PAGE}&offset=${currentOffset}`;
      if (letter) {
        url += `&letter=${letter}`;
      } else if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        if (reset) {
          setCompanies(data.companies);
          setOffset(data.companies.length);
        } else {
          setCompanies(prev => [...prev, ...data.companies]);
          setOffset(prev => prev + data.companies.length);
        }
        setTotalCompanies(data.total);
        setHasMore(currentOffset + data.companies.length < data.total);
      }
    } catch (error) {
      console.error("❌ Ошибка загрузки компаний:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setTimeout(() => setIsTransitioning(false), 100);
    }
  };

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    loadCompanies(searchQuery, false);
  };

  // Загрузка вакансий компании
  const loadCompanyJobs = async (companyName) => {
    setJobsLoading(true);
    try {
      const url = `${apiUrl("jobs.php")}?limit=50&company=${encodeURIComponent(companyName)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setCompanyJobs(data.jobs);
      }
    } catch (error) {
      console.error("Ошибка загрузки вакансий компании:", error);
    } finally {
      setJobsLoading(false);
    }
  };

  // Обработчик выбора компании
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    loadCompanyJobs(company.company_name);

    // Изменяем URL на /company/:companyName и добавляем в историю браузера
    navigate(`/company/${encodeURIComponent(company.company_name)}`, {
      replace: false, // false = добавляет в историю, true = заменяет текущую запись
      state: { fromCompaniesPage: true } // Сохраняем состояние
    });

    // Сохраняем выбранную компанию в localStorage для навигации назад
    localStorage.setItem('selectedCompany', JSON.stringify({
      name: company.company_name,
      logo: company.logo_url,
      jobCount: company.job_count
    }));

    if (isMobile) {
      setShowMobileModal(true);
    }
  };

  const handleLetterClick = (letter) => {
    if (activeLetter === letter) {
      setActiveLetter("");
      setSearchQuery("");
      loadCompanies("", true, null);
    } else {
      setActiveLetter(letter);
      setSearchQuery("");
      loadCompanies("", true, letter);
    }
  };

  // Обработчик поиска
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setActiveLetter("");

    // Дебаунс поиска
    const timer = setTimeout(() => {
      loadCompanies(value, true, null);
    }, 500);

    return () => clearTimeout(timer);
  };

  // Инициализация
  useEffect(() => {
    if (companyName) {
      // Прямой заход на /company/Name — грузим вакансии сразу
      const decodedName = decodeURIComponent(companyName);
      setSelectedCompany({ company_name: decodedName, logo_url: null, job_count: 0 });
      loadCompanyJobs(decodedName);
    }
    loadCompanies("", true, null);
  }, []);

  // Обработка изменения URL (включая навигацию браузера назад/вперед)
  useEffect(() => {
    if (companyName) {
      const decodedCompanyName = decodeURIComponent(companyName);
      
      // Если уже выбрана та же компания — ничего не делаем
      if (selectedCompany?.company_name === decodedCompanyName) return;
      
      // Пытаемся найти компанию в загруженном списке
      const companyToSelect = companies.find(c => c.company_name === decodedCompanyName);
      
      if (companyToSelect) {
        setSelectedCompany(companyToSelect);
        loadCompanyJobs(companyToSelect.company_name);
      } else if (companies.length > 0) {
        // Компании загружены, но такой нет — показываем список
        setSelectedCompany(null);
        setCompanyJobs([]);
      }
      // Если companies ещё не загружены — ничего не делаем, useEffect сработает снова когда companies обновится
    } else {
      // Если companyName нет в URL - показываем общий список компаний
      setSelectedCompany(null);
      setCompanyJobs([]);
    }
  }, [companyName, companies, location.pathname]);

  // Если выбрана компания и не мобилка - показываем вакансии
  if (selectedCompany && !isMobile) {
    return (
      <Box maxWidth="1200px" mx="auto" mt={3} p={2}>
        {/* Хлебные крошки */}
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              // Просто меняем URL, useEffect сам обработает изменение
              navigate('/companies', { replace: false });
            }}
            sx={{ textTransform: "none" }}
          >
            Все компании
          </Button>
          <Typography variant="body2" color="text.secondary">
            /
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {selectedCompany.company_name}
          </Typography>
        </Stack>

        {/* Заголовок компании */}
        <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Stack direction="row" spacing={3} alignItems="center">
            {selectedCompany.logo_url && (
              <Box
                component="img"
                src={selectedCompany.logo_url}
                alt={selectedCompany.company_name}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  objectFit: "contain",
                  backgroundColor: "white",
                  p: 1,
                  border: "1px solid rgba(0,0,0,0.1)"
                }}
              />
            )}

            <Box flex={1}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {selectedCompany.company_name}
              </Typography>

              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip
                  icon={<Work />}
                  label={`${companyJobs.length} вакансий`}
                  color="primary"
                  variant="outlined"
                />

                {selectedCompany.categories && (
                  <Chip
                    icon={<Category />}
                    label={selectedCompany.categories.split(",")[0].trim()}
                    variant="outlined"
                  />
                )}

                <Chip
                  icon={<CalendarToday />}
                  label={`Активна с ${new Date(selectedCompany.first_job_date).toLocaleDateString("ru-RU")}`}
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {/* Список вакансий */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Вакансии компании ({companyJobs.length})
        </Typography>

        {jobsLoading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        ) : companyJobs.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="h6" color="text.secondary">
              У этой компании пока нет активных вакансий
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {companyJobs.map((job) => (
              <Grid item xs={12} key={job.id}>
                <CustomCard
                  id={job.id}
                  image={job.image_url}
                  title={job.title}
                  description={job.description}
                  salary={job.salary}
                  city={job.location || job.city}
                  dateAdded={job.created_at}
                  company={job.company}
                  onMoreClick={() => {
                    // Сохраняем что мы переходим из списка вакансий компании
                    localStorage.setItem('fromCompanyJobs', 'true');
                    localStorage.setItem('selectedCompany', JSON.stringify({
                      name: selectedCompany.company_name,
                      logo: selectedCompany.logo_url,
                      jobCount: selectedCompany.job_count
                    }));
                    // Сохраняем название компании для возврата
                    localStorage.setItem('currentCompanyId', selectedCompany.company_name);
                    navigate(`/job/${job.id}`, { replace: false });
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  // Основная страница компаний
  return (
    <Box maxWidth="1200px" mx="auto" mt={{ xs: 0, md: 3 }} p={{ xs: 0, md: 2 }}>
      {/* Заголовок */}
      <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ px: { xs: 2, md: 0 }, mt: { xs: 2, md: 0 } }}>
        Компании
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Найдите работодателя по названию или просмотрите все компании с активными вакансиями
      </Typography>

      {/* Поиск */}
      <Paper sx={{ p: 2, borderRadius: { xs: 0, md: 2 }, mb: 3, mx: { xs: 0, md: 0 } }}>
        <TextField
          fullWidth
          placeholder="Поиск компании..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2, px: { xs: 2, md: 0 } }}
        />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Найдено компаний: {totalCompanies}
          </Typography>

          {isMobile && (
            <Button
              startIcon={<FilterList />}
              variant="outlined"
              size="small"
              onClick={() => {/* Можно добавить дополнительные фильтры */}}
            >
              Фильтры
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Список компаний */}
      {loading ? (
        <Box textAlign="center" py={4}>
          <CircularProgress />
        </Box>
      ) : null}

      {/* Алфавитная панель */}
      {!selectedCompany && (
        <Box sx={{ overflowX: "auto", mb: 2 }}>
          <Stack direction="row" spacing={0.5} sx={{ minWidth: "max-content", px: 0.5 }}>
            <Button
              size="small"
              variant={activeLetter === "" ? "contained" : "text"}
              onClick={() => handleLetterClick("")}
              sx={{ minWidth: 32, height: 32, fontSize: 12, fontWeight: activeLetter === "" ? 700 : 400 }}
            >
              Все
            </Button>
            {LATIN_LETTERS.map(letter => (
              <Button
                key={letter}
                size="small"
                variant={activeLetter === letter ? "contained" : "text"}
                onClick={() => handleLetterClick(letter)}
                sx={{ minWidth: 32, height: 32, fontSize: 12, fontWeight: activeLetter === letter ? 700 : 400 }}
              >
                {letter}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {loading ? (
        <Box textAlign="center" py={4}>
          <CircularProgress />
        </Box>
      ) : companies.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Business sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Компании не найдены
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Попробуйте изменить поисковый запрос
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 0.5, sm: 1, md: 2 },
            justifyContent: { xs: "stretch", md: "flex-start" },
            opacity: isTransitioning ? 0.5 : 1,
            transition: "opacity 0.2s ease",
          }}
        >
          {companies.map((company) => (
            <Box
              key={company.company_name}
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)", md: "0 0 calc(25% - 12px)", lg: "0 0 calc(25% - 12px)" },
                maxWidth: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(25% - 12px)", lg: "calc(25% - 12px)" },
                boxSizing: "border-box",
              }}
            >
              <Card
                sx={{
                  borderRadius: 2,
                  height: "100%",
                  width: "100%",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)"
                  },
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxSizing: "border-box"
                }}
                onClick={() => handleCompanySelect(company)}
              >
                {/* Логотип компании */}
                <Box
                  sx={{
                    height: 120,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    backgroundColor: "white",
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                    overflow: "hidden"
                  }}
                >
                  {company.logo_url ? (
                    <Box
                      component="img"
                      src={company.logo_url ? (company.logo_url.startsWith('http') ? company.logo_url : fileUrl(company.logo_url)) : ''}
                      alt={company.company_name}
                      sx={{
                        maxWidth: "90%",
                        maxHeight: "90%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                        margin: "0 auto"
                      }}
                      onLoad={(e) => {
                        // Автоматическое масштабирование если логотип слишком большой
                        const img = e.target;
                        const container = img.parentElement;
                        if (img.naturalWidth > container.clientWidth * 0.9) {
                          img.style.width = "90%";
                          img.style.height = "auto";
                        }
                        if (img.naturalHeight > container.clientHeight * 0.9) {
                          img.style.height = "90%";
                          img.style.width = "auto";
                        }
                      }}
                    />
                  ) : (
                    <Business
                      sx={{
                        fontSize: 50,
                        color: "text.secondary",
                        width: "60px",
                        height: "60px"
                      }}
                    />
                  )}
                </Box>

                <CardContent sx={{
                  flexGrow: 1,
                  p: 2,
                  minHeight: "140px",
                  flexShrink: 0,
                  width: "100%",
                  boxSizing: "border-box"
                }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      lineHeight: 1.2,
                      minHeight: "2.4em",
                      maxHeight: "2.4em",
                      wordBreak: "break-word",
                      width: "100%"
                    }}
                    title={company.company_name}
                  >
                    {company.company_name}
                  </Typography>

                  <Stack spacing={1}>
                    <Chip
                      icon={<Work />}
                      label={`${company.job_count} вакансий`}
                      size="small"
                      color="primary"
                      sx={{ width: "fit-content" }}
                    />

                    {company.categories && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.8rem",
                          lineHeight: 1.2,
                          height: "2.4em",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical"
                        }}
                        title={company.categories}
                      >
                        {company.categories}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Активна с {new Date(company.first_job_date).toLocaleDateString("ru-RU")}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Кнопка "Загрузить ещё" */}
      {!loading && companies.length > 0 && hasMore && (
        <Box textAlign="center" mt={4} mb={2}>
          <Button
            variant="outlined"
            size="large"
            onClick={loadMore}
            disabled={loadingMore}
            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
          >
            {loadingMore ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
            Загрузить ещё
          </Button>
          <Typography variant="caption" display="block" mt={1} color="text.secondary">
            Показано {companies.length} из {totalCompanies}
          </Typography>
        </Box>
      )}

      {/* Модальное окно для мобильных (вакансии компании) */}
      <Dialog
        fullScreen={isMobile}
        open={showMobileModal && isMobile}
        onClose={() => setShowMobileModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => {
              setShowMobileModal(false);
              // Если мы на странице конкретной компании, возвращаемся к общему списку
              if (companyName) {
                navigate('/companies', { replace: false });
              }
            }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight="bold">
              {selectedCompany?.company_name}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {selectedCompany && (
            <>
              {/* Информация о компании */}
              <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                {selectedCompany.logo_url && (
                  <Box
                    component="img"
                    src={selectedCompany.logo_url ? (selectedCompany.logo_url.startsWith('http') ? selectedCompany.logo_url : fileUrl(selectedCompany.logo_url)) : ''}
                    alt={selectedCompany.company_name}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      objectFit: "contain"
                    }}
                  />
                )}

                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedCompany.company_name}
                  </Typography>
                  <Chip
                    label={`${selectedCompany.job_count} вакансий`}
                    size="small"
                    color="primary"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Stack>

              {/* Список вакансий */}
              <Typography variant="h6" gutterBottom>
                Вакансии
              </Typography>

              {jobsLoading ? (
                <Box textAlign="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : companyJobs.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  Нет активных вакансий
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {companyJobs.map((job) => (
                    <CustomCard
                      key={job.id}
                      id={job.id}
                      image={job.image_url}
                      title={job.title}
                      description={job.description}
                      salary={job.salary}
                      city={job.location || job.city}
                      dateAdded={job.created_at}
                      company={job.company}
                      onMoreClick={() => {
                        // Сохраняем что мы переходим из списка вакансий компании
                        localStorage.setItem('fromCompanyJobs', 'true');
                        localStorage.setItem('selectedCompany', JSON.stringify({
                          name: selectedCompany.company_name,
                          logo: selectedCompany.logo_url,
                          jobCount: selectedCompany.job_count
                        }));
                        // Сохраняем название компании для возврата
                        localStorage.setItem('currentCompanyId', selectedCompany.company_name);
                        setShowMobileModal(false);
                        navigate(`/job/${job.id}`, { replace: false });
                      }}

                    />
                  ))}
                </Stack>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowMobileModal(false)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesPage;